import { useMediaQuery } from "react-responsive";
import { motion } from "framer-motion";
import React from "react";
import "../styles/global.css";
import { Story } from "../types/StoryTypes";
import ModernCard from "./ui/ModernCard"; // Import ModernCard

const StoryTimeline = ({ stories }: { stories: Story[] }) => {
  // Use react-responsive to detect mobile devices
  const isMobile = useMediaQuery({ maxWidth: 768 });

  // If on mobile, render ModernCards instead of timeline
  if (isMobile) {
    return (
      <div className="story-grid">
        {stories.map((story, index) => (
          <motion.div
            key={story.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="story-grid-item"
          >
            <ModernCard
              title={story.category || "Personal Story"} // Use category if available
              content={story.text}
              link={story.xLink || null}
              date={story.createdAt?.toDate 
                ? story.createdAt.toDate().toLocaleDateString() 
                : undefined}
              category={story.category}
              // Generate a different color based on category or index if you want variety
              color={getCategoryColor(story.category, index)}
            />
          </motion.div>
        ))}
      </div>
    );
  }

  // On desktop, continue to use the timeline view
  return (
    <div className="story-timeline">
      {stories.map((story, index) => (
        <motion.div
          key={story.id}
          className="timeline-item"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <div className="timeline-dot"></div>
          <div className="timeline-content">
            <div className="timeline-date">
              {story.createdAt?.toDate
                ? story.createdAt.toDate().toLocaleDateString()
                : "Date unavailable"}
            </div>
            <div className="timeline-card card">
              <p>{story.text}</p>

              {story.xLink && (
                <a
                  href={story.xLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="x-link"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                  </svg>
                  <span>View on X</span>
                </a>
              )}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Helper function to assign colors based on category or index
const getCategoryColor = (category?: string, index: number = 0) => {
  // Define color palette for categories
  const categoryColors: Record<string, string> = {
    "Shared Experience": "#4a6fa5",
    "Treatment Journey": "#47b881",
    "Recovery Story": "#9c6dde",
    "Alternative Therapy": "#e95c33",
    "Personal Insight": "#f7d154",
    "Medical Update": "#38a2c8",
  };

  // Return the appropriate color or a fallback based on index
  if (category && categoryColors[category]) {
    return categoryColors[category];
  }

  // Fallback colors that cycle through
  const fallbackColors = ["#4a6fa5", "#47b881", "#9c6dde", "#e95c33", "#38a2c8"];
  return fallbackColors[index % fallbackColors.length];
};

export default StoryTimeline;
